/**
 * SENTRY: errors and page timings from the browser (staff app, booking page, portal).
 * Imported first in main.tsx, so it is running before anything else can throw.
 *
 * ⚠️ THIS APP HOLDS HEALTH DATA, so what leaves for Sentry is kept to what diagnoses a fault:
 *
 *   - no user details, cookies, headers, query strings or request bodies (`dataCollection`);
 *     no Sentry.setUser anywhere. Patients' IP addresses are not collected either.
 *   - every address is cleaned (`clean` below): the query string and fragment go (they carry
 *     `?find=<patient name>`, `?q=<search>`), ids in the path become `:id`, and long tokens in
 *     the path (portal, payslip and share links) become `:token`.
 *   - console breadcrumbs are dropped (a logged object can hold anything) and clicks keep
 *     only the element's tag and classes, never its attributes (a title can hold a name).
 *   - NO SESSION REPLAY. It records the screen, and the screen is patient records. Do not
 *     add `replayIntegration` without the practice agreeing to it.
 *
 * Sentry's own address is allowed in the Content-Security-Policy's connect-src
 * (ops/nginx-csp.conf); without that the browser silently refuses to send anything.
 *
 * The DSN is public by design (every browser gets it) and lives in webclient/.env.production.
 * `environment` comes from the address: staging, demo, or production (every practice's own
 * address), with the practice's address as a tag so one practice's errors can be picked out.
 */
import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'

const host = window.location.hostname
const environment = host === 'localhost' || host === '127.0.0.1' ? 'development'
  : host.startsWith('staging.') ? 'staging' : host.startsWith('demo.') ? 'demo' : 'production'

/** An address with nothing personal left in it. */
export function clean(url: string): string {
  const [path] = String(url).split(/[?#]/)
  return path
    .replace(/\/[0-9a-f]{24}(?=\/|$)/gi, '/:id')          // database ids
    .replace(/\/\d{3,}(?=\/|$)/g, '/:id')                 // Dentally patient and other numeric ids
    .replace(/\/[A-Za-z0-9_-]{16,}(?=\/|$)/g, '/:token')  // link tokens (/f/, /pay/, /share/...)
}

/** Addresses inside free text (an error message, a span's description). */
const cleanText = (s: string) => s
  .replace(/https?:\/\/[^\s'")]+/g, (u) => clean(u))
  // A bare path with a query string ("/booking/requests?status=pending"). No lookbehind: older
  // iPhones (iOS before 16.4) cannot parse one, and a parse error here would stop the whole app.
  .replace(/(^|[\s'"(=:])(\/[^\s'")?]*\?[^\s'")]*)/g, (_m, pre: string, u: string) => pre + clean(u))

/** Query strings and fragments are dropped outright wherever they are kept on their own. */
const QUERY_KEY = /(^|[._])(query|query_string|fragment|search)$/i
/** Left exactly as they are: code file names and line numbers, which Sentry needs to show the source. */
const KEEP = new Set(['stacktrace', 'debug_meta', 'sdk', 'sdkProcessingMetadata'])

/**
 * Every string anywhere in the event, cleaned: an address is reduced by `clean`, free text has
 * its addresses reduced, and any field that is only a query string goes. Walking the whole
 * event rather than listing fields is deliberate: tracing adds new fields (`url.full` was the
 * one that first leaked a searched-for name in testing) and a list would miss the next one.
 */
function deepClean(v: unknown, key = '', depth = 0): unknown {
  if (depth > 12 || v == null) return v
  if (typeof v === 'string') return /^https?:\/\//.test(v) || (v.startsWith('/') && /url|path|route|from|to|transaction|name/i.test(key)) ? clean(v) : cleanText(v)
  if (Array.isArray(v)) return v.map((x) => deepClean(x, key, depth + 1))
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>
    for (const k of Object.keys(o)) {
      if (KEEP.has(k)) continue
      if (QUERY_KEY.test(k)) { delete o[k]; continue }
      o[k] = deepClean(o[k], k, depth + 1)
    }
  }
  return v
}

function scrub<T extends Sentry.Event>(event: T): T {
  if (event.request) {
    delete event.request.query_string
    delete event.request.cookies
    delete event.request.headers
    delete event.request.data
  }
  for (const b of event.breadcrumbs ?? []) cleanBreadcrumb(b)
  return deepClean(event) as T
}

function cleanBreadcrumb(b: Sentry.Breadcrumb): Sentry.Breadcrumb {
  if (b.data) {
    for (const k of ['url', 'from', 'to']) if (typeof b.data[k] === 'string') b.data[k] = clean(b.data[k])
    delete b.data['http.query']
    delete b.data['http.fragment']
  }
  // "div.card > button.rounded[title="Jane Smith"]" keeps the tags and classes only.
  if (b.category?.startsWith('ui.') && b.message) b.message = b.message.replace(/\[[^\]]*\]/g, '')
  return b
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment,
  release: import.meta.env.VITE_APP_VERSION,
  initialScope: { tags: { practice: host } },

  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: { request: false, response: false },
    queryParams: false,
    httpBodies: [],
  },

  integrations: [
    // Names page loads and navigations by route ("/booking/:practitionerId"), not by the
    // address, which would carry ids (Routes is wrapped for this in App.tsx).
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes,
    }),
  ],

  // Tracing: every page load in testing, a fifth of them in production.
  tracesSampleRate: environment === 'production' ? 0.2 : 1.0,
  // Only our own API gets the trace headers; nothing is sent to anybody else's server.
  tracePropagationTargets: [/^\/api\//],

  ignoreErrors: [
    // Browsers report these when a layout settles; they are not faults.
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
  ],

  beforeBreadcrumb(b) {
    if (b.category === 'console') return null
    return cleanBreadcrumb(b)
  },
  beforeSend: (event) => scrub(event),
  beforeSendTransaction: (event) => scrub(event),
})
